import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
    Rocket,
    Bot,
    TrendingUp,
    DollarSign,
    Play,
    Square,
    RefreshCw,
    Plus,
    Settings,
    Activity,
    BarChart3
} from 'lucide-react';
import AdminNavbar from '../components/AdminNavbar';

const AdminTokenLaunchPage = () => {
    const [loading, setLoading] = useState(false);
    const [customTokens, setCustomTokens] = useState([]);
    const [tradingPairs, setTradingPairs] = useState([]);
    const [botConfigs, setBotConfigs] = useState([]);
    const [activeBots, setActiveBots] = useState([]);

    // Token launch form
    const [tokenForm, setTokenForm] = useState({
        symbol: '',
        name: '',
        contractAddress: '',
        decimals: 18,
        initialPrice: '',
        totalSupply: '',
        circulatingSupply: '',
        logo: '',
        createUSDTPair: true,
        enableBot: false,
        botTargetVolume: 5000
    });

    // Bot control form
    const [selectedPair, setSelectedPair] = useState('');
    const [targetVolume, setTargetVolume] = useState(5000);

    const volumeOptions = [
        { value: 5000, label: '5,000 USDT/day' },
        { value: 10000, label: '10,000 USDT/day' },
        { value: 15000, label: '15,000 USDT/day' },
        { value: 20000, label: '20,000 USDT/day' }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [tokensRes, pairsRes, botsRes, activeRes] = await Promise.all([
                api.get('/admin/tokens/custom'),
                api.get('/admin/trading-pairs'),
                api.get('/admin/trading-bot/configs'),
                api.get('/admin/trading-bot/active')
            ]);

            setCustomTokens(tokensRes.data.tokens || []);
            setTradingPairs(pairsRes.data.tradingPairs || []);
            setBotConfigs(botsRes.data.configs || []);
            setActiveBots(activeRes.data.bots || []);
        } catch (error) {
            console.error('Fetch data error:', error);
        }
    };

    const handleLaunchToken = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await api.post('/admin/tokens/launch', tokenForm);
            toast.success('Token launched successfully!');
            setTokenForm({
                symbol: '',
                name: '',
                contractAddress: '',
                decimals: 18,
                initialPrice: '',
                totalSupply: '',
                circulatingSupply: '',
                logo: '',
                createUSDTPair: true,
                enableBot: false,
                botTargetVolume: 5000
            });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to launch token');
        } finally {
            setLoading(false);
        }
    };

    const handleStartBot = async () => {
        if (!selectedPair) {
            toast.error('Please select a trading pair');
            return;
        }

        setLoading(true);
        try {
            await api.post('/admin/trading-bot/start', {
                tradingPairId: selectedPair,
                targetVolume
            });
            toast.success('Trading bot started!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to start bot');
        } finally {
            setLoading(false);
        }
    };

    const handleStopBot = async (pairId) => {
        setLoading(true);
        try {
            await api.post('/admin/trading-bot/stop', { tradingPairId: pairId });
            toast.success('Trading bot stopped!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to stop bot');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900">
            <AdminNavbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Rocket className="w-8 h-8 text-purple-500" />
                        <h1 className="text-2xl font-bold text-white">Token Launch & Bot Control</h1>
                    </div>
                    <button
                        onClick={fetchData}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Token Launch Form */}
                    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                        <div className="flex items-center gap-2 mb-6">
                            <Plus className="w-5 h-5 text-green-500" />
                            <h2 className="text-xl font-semibold text-white">Launch New Token</h2>
                        </div>

                        <form onSubmit={handleLaunchToken} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Symbol *</label>
                                    <input
                                        type="text"
                                        value={tokenForm.symbol}
                                        onChange={(e) => setTokenForm({ ...tokenForm, symbol: e.target.value.toUpperCase() })}
                                        placeholder="e.g., MYTOKEN"
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Name *</label>
                                    <input
                                        type="text"
                                        value={tokenForm.name}
                                        onChange={(e) => setTokenForm({ ...tokenForm, name: e.target.value })}
                                        placeholder="e.g., My Custom Token"
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Contract Address (BEP20)</label>
                                <input
                                    type="text"
                                    value={tokenForm.contractAddress}
                                    onChange={(e) => setTokenForm({ ...tokenForm, contractAddress: e.target.value })}
                                    placeholder="0x..."
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Initial Price (USDT) *</label>
                                    <input
                                        type="number"
                                        step="0.00000001"
                                        value={tokenForm.initialPrice}
                                        onChange={(e) => setTokenForm({ ...tokenForm, initialPrice: e.target.value })}
                                        placeholder="0.01"
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Decimals</label>
                                    <input
                                        type="number"
                                        value={tokenForm.decimals}
                                        onChange={(e) => setTokenForm({ ...tokenForm, decimals: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Total Supply</label>
                                    <input
                                        type="number"
                                        value={tokenForm.totalSupply}
                                        onChange={(e) => setTokenForm({ ...tokenForm, totalSupply: e.target.value })}
                                        placeholder="1000000000"
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Circulating Supply</label>
                                    <input
                                        type="number"
                                        value={tokenForm.circulatingSupply}
                                        onChange={(e) => setTokenForm({ ...tokenForm, circulatingSupply: e.target.value })}
                                        placeholder="500000000"
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Logo URL</label>
                                <input
                                    type="text"
                                    value={tokenForm.logo}
                                    onChange={(e) => setTokenForm({ ...tokenForm, logo: e.target.value })}
                                    placeholder="https://..."
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={tokenForm.createUSDTPair}
                                        onChange={(e) => setTokenForm({ ...tokenForm, createUSDTPair: e.target.checked })}
                                        className="w-4 h-4 text-purple-500 rounded focus:ring-purple-500"
                                    />
                                    Create USDT pair
                                </label>
                                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={tokenForm.enableBot}
                                        onChange={(e) => setTokenForm({ ...tokenForm, enableBot: e.target.checked })}
                                        className="w-4 h-4 text-purple-500 rounded focus:ring-purple-500"
                                    />
                                    Enable trading bot
                                </label>
                            </div>

                            {tokenForm.enableBot && (
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Bot Target Volume</label>
                                    <select
                                        value={tokenForm.botTargetVolume}
                                        onChange={(e) => setTokenForm({ ...tokenForm, botTargetVolume: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                                    >
                                        {volumeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg text-white font-semibold transition disabled:opacity-50"
                            >
                                {loading ? 'Launching...' : 'Launch Token'}
                            </button>
                        </form>
                    </div>

                    {/* Bot Control Panel */}
                    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                        <div className="flex items-center gap-2 mb-6">
                            <Bot className="w-5 h-5 text-blue-500" />
                            <h2 className="text-xl font-semibold text-white">Trading Bot Control</h2>
                        </div>

                        <div className="space-y-4 mb-6">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Select Trading Pair</label>
                                <select
                                    value={selectedPair}
                                    onChange={(e) => setSelectedPair(e.target.value)}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                                >
                                    <option value="">Choose a pair...</option>
                                    {tradingPairs.map(pair => (
                                        <option key={pair.id} value={pair.id}>
                                            {pair.symbol} {pair.baseToken?.isCustom ? '(Custom)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Daily Target Volume</label>
                                <select
                                    value={targetVolume}
                                    onChange={(e) => setTargetVolume(parseInt(e.target.value))}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                                >
                                    {volumeOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleStartBot}
                                disabled={loading || !selectedPair}
                                className="w-full py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                <Play className="w-4 h-4" />
                                Start Bot
                            </button>
                        </div>

                        {/* Active Bots */}
                        <div>
                            <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
                                <Activity className="w-4 h-4 text-green-400" />
                                Active Bots ({activeBots.length})
                            </h3>

                            {activeBots.length === 0 ? (
                                <p className="text-gray-500 text-center py-4">No active bots</p>
                            ) : (
                                <div className="space-y-2">
                                    {activeBots.map(bot => (
                                        <div key={bot.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                                            <div>
                                                <p className="text-white font-medium">{bot.tradingPair?.symbol}</p>
                                                <p className="text-sm text-gray-400">
                                                    Target: {parseFloat(bot.targetVolume).toLocaleString()} USDT/day
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => handleStopBot(bot.tradingPairId)}
                                                className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm flex items-center gap-1"
                                            >
                                                <Square className="w-3 h-3" />
                                                Stop
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Custom Tokens List */}
                <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="w-5 h-5 text-yellow-500" />
                        <h2 className="text-xl font-semibold text-white">Launched Tokens ({customTokens.length})</h2>
                    </div>

                    {customTokens.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No custom tokens launched yet</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-gray-400 text-sm border-b border-gray-700">
                                        <th className="text-left py-3 px-4">Token</th>
                                        <th className="text-left py-3 px-4">Blockchain</th>
                                        <th className="text-right py-3 px-4">Initial Price</th>
                                        <th className="text-right py-3 px-4">Total Supply</th>
                                        <th className="text-center py-3 px-4">Status</th>
                                        <th className="text-left py-3 px-4">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customTokens.map(token => (
                                        <tr key={token.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    {token.logo && (
                                                        <img src={token.logo} alt={token.symbol} className="w-6 h-6 rounded-full" />
                                                    )}
                                                    <div>
                                                        <p className="text-white font-medium">{token.symbol}</p>
                                                        <p className="text-sm text-gray-400">{token.name}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-gray-300">{token.blockchain}</td>
                                            <td className="py-3 px-4 text-right text-white">
                                                ${parseFloat(token.initialPrice || 0).toFixed(8)}
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-300">
                                                {token.totalSupply ? parseFloat(token.totalSupply).toLocaleString() : '-'}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded text-xs ${token.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                    {token.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-gray-400 text-sm">
                                                {new Date(token.createdAt).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Bot Statistics */}
                <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <div className="flex items-center gap-2 mb-6">
                        <BarChart3 className="w-5 h-5 text-cyan-500" />
                        <h2 className="text-xl font-semibold text-white">Bot Configurations ({botConfigs.length})</h2>
                    </div>

                    {botConfigs.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">No bot configurations yet</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="text-gray-400 text-sm border-b border-gray-700">
                                        <th className="text-left py-3 px-4">Trading Pair</th>
                                        <th className="text-right py-3 px-4">Target Volume</th>
                                        <th className="text-right py-3 px-4">Volume Generated</th>
                                        <th className="text-center py-3 px-4">Status</th>
                                        <th className="text-left py-3 px-4">Last Trade</th>
                                        <th className="text-center py-3 px-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {botConfigs.map(config => (
                                        <tr key={config.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                                            <td className="py-3 px-4 text-white font-medium">
                                                {config.tradingPair?.symbol || 'Unknown'}
                                            </td>
                                            <td className="py-3 px-4 text-right text-gray-300">
                                                ${parseFloat(config.targetVolume || 0).toLocaleString()}/day
                                            </td>
                                            <td className="py-3 px-4 text-right text-cyan-400">
                                                ${parseFloat(config.totalVolumeGenerated || 0).toLocaleString()}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <span className={`px-2 py-1 rounded text-xs ${config.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                                                    {config.isActive ? 'Running' : 'Stopped'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-gray-400 text-sm">
                                                {config.lastTradeAt ? new Date(config.lastTradeAt).toLocaleString() : 'Never'}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {config.isActive ? (
                                                    <button
                                                        onClick={() => handleStopBot(config.tradingPairId)}
                                                        className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-white text-sm"
                                                    >
                                                        Stop
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedPair(config.tradingPairId);
                                                            setTargetVolume(parseFloat(config.targetVolume));
                                                        }}
                                                        className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-white text-sm"
                                                    >
                                                        Configure
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminTokenLaunchPage;
